import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TabBox from './TabBox';
import List from './List';
import EditForm from './EditForm';
import RestaurantListed from '../molecules/RestaurantListed';
import Property from '../atoms/Property';
import Input from '../atoms/Input';
import Button from '../atoms/Button';
import Upload from '../molecules/Upload';
import Download from '../molecules/Download';
import {
  createVenueFile,
  deleteVenueFile,
} from '../../features/venue/venueSlice';
import { isImperial, metricsName } from '../../features/metrics/metricsSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { deleteFile, uploadFile } from '../../lib/apis/file';
import { updateVenueState } from '../../features/venue/venueSlice';
import { updateVenue } from '../../lib/apis/venue';
import { display } from '../../data/display';
import { byProperty, clearObj, getErrorMsg } from '../../lib/utils';
import { convertValueToImperial, metricUnitKeys, Unit } from '../../lib/units';
import styles from '../../styles/components/organisms/VenueProperties.module.scss';
import { objectTraps } from 'immer/dist/internal';

interface VenuePropertiesProps {
  venueID: string;
  venuePlan: string;
  data: any;
  access?: boolean;
  setLayer: (el: any) => void;
  apply: (id: string) => void;
}

export default function VenueProperties(props: VenuePropertiesProps) {
  const { t } = useTranslation(['common', 'venue']);
  const dispatch = useAppDispatch();
  const imperial = useAppSelector(isImperial);
  const unitsType = useAppSelector(metricsName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [files, setFiles] = useState([]) as any;
  const [removedFiles, setRemovedFiles] = useState([]) as any;
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState('general');
  const { venueID, data, access, venuePlan } = props;
  const formID = 'propertiesForm';
  const allFields = display.venue.platinum as any;
  const planFields = (() => {
    switch (true) {
      case venuePlan === 'BASIC':
        return display.venue.basic;
      case venuePlan === 'PLATINUM':
      case venuePlan === 'TRIAL_PLATINUM':
      case Boolean(access):
      default:
        return display.venue.platinum;
    }
  })();
  const planData = access ? allFields : planFields;

  /*
   * Properties
   */
  // Handle merged fields
  const exclude = [] as any; //edit ? ['hall_min_max_m2'] : ['hall_min_m2', 'hall_max_m2'];

  // Get fields to display
  const displayObj = allFields.properties;
  const planDisplayObj = planData.properties;

  // Tabs
  const tabKeys = Object.keys(displayObj);
  const tabs = tabKeys.map((key) => ({
    id: key,
    label: t(`venue:${key}`),
  }));

  // Properties by tab
  const propertiesByTab = displayObj[tab] || {};
  const planDataByTab = planDisplayObj[tab] || {};
  // filter merged fields
  const propertyKeys = Object.keys(propertiesByTab).filter(
    (el) => exclude.indexOf(el) === -1
  );

  const properties = propertyKeys.map((key: string) => {
    const defaults = propertiesByTab[key];
    const value =
      data[key] || (data[key] === false ? false : defaults.value || '');
    const { maxLength, exp } = defaults;
    const metricUnitKey = metricUnitKeys[unitsType][defaults.unit as Unit];
    const placeholder =
      maxLength && !!exp
        ? t(`common:${exp}`, { maxLength })
        : typeof exp === 'number'
        ? `${t(`common:e.g.`)} ${exp}`
        : !!exp
        ? t(`common:${exp}`)
        : '';

    const fieldObj = {
      ...defaults,
      id: key,
      label: t(`venue:${key}`),
      unit: t(`common:${metricUnitKey || defaults.unit}`),
      convertTo: !!metricUnitKey && imperial ? defaults.unit : '',
      placeholder,
      // restricted: !!access && !planFields.properties[tab]?.[key],
      value: !planDataByTab[key]
        ? defaults.value
        : imperial && !!metricUnitKey
        ? convertValueToImperial(value, defaults.unit)
        : value,
    };

    return fieldObj;
  });

  // Retaurants
  const currRestaurantsData = allFields.restaurants || [];
  const currRestaurants = currRestaurantsData
    .map((restaurant: any) => {
      const currRestaurantKeys = Object.keys(restaurant);
      const specs = currRestaurantKeys
        .filter((key) => !['name', 'description'].some((el) => el === key))
        .map((key) => {
          return {
            id: key,
            label: t(`venue:spaces:${key}`),
            value:
              typeof restaurant[key] === 'boolean'
                ? t(`venue:${restaurant[key]}`)
                : restaurant[key],
            unit: '',
          };
        });
      return {
        name: restaurant.name,
        description: restaurant.description,
        specs,
      };
    })
    .sort(byProperty('capacity', 'desc'));

  /**
   * Handle errors
   */
  const handleError = (error: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle Files
   */

  const onFileRemove = (file: any) => {
    const { id } = file;
    if (!id) {
      return;
    }
    // console.log('onFileRemove', id);
    setRemovedFiles([...removedFiles.filter((str: string) => str !== id), id]);
  };

  const updateFiles = async () => {
    for (const id of removedFiles) {
      await deleteFile(id)
        .then((response: any) => {
          // console.log('deletedFile', id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }

    const newFiles = files || [];
    if (!!newFiles.length) {
      const uploaded = [] as any;
      // console.log('upload files', newFiles);
      for (const file of newFiles) {
        const obj = clearObj({
          file,
          ...file,
          category: 'venue-map',
          venue_id: venueID,
          // caption: fields.description,
          // type: fields.type,
          // image: '',
          // url: fields.website,
        });
        // console.log('uploadFile', obj);
        // setLoading(false);
        // return;
        uploadFile(obj)
          .then((response: any) => {
            // logoField.value = '';
            uploaded.push(response);
            // console.log('uploaded file', response);
            if (uploaded.length === newFiles.length) {
              // field.value = '';
              completeSubmit(response);
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else if (!!removedFiles.length) {
      completeSubmit();
    } else {
      setLoading(false);
      setError(t('common:form-input-error'));
    }
  };

  const completeSubmit = (result?: any) => {
    // console.log('completeSubmit', result);
    setFiles([]);
    setRemovedFiles([]);
    if (!!result) {
      dispatch(createVenueFile(result));
    } else {
      // console.log('deleteVenueFile', removedFiles[0]);
      dispatch(deleteVenueFile(removedFiles[0]));
    }

    // dispatch(updateVenueFile(result));
    setLoading(false);
    setEdit(false);
  };

  /**
   * Handle form submit
   */
  const submit = async (formData: any) => {
    setError(false);
    setLoading(true);
    if (tab == 'map') {
      updateFiles();
    } else {
      try {
        const venue = await updateVenue(venueID, formData);
        dispatch(updateVenueState(venue));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        setEdit(false);
      }
    }
  };

  const onTabChange = (id: string) => {
    if (id !== tab) {
      setTab(id);
      setEdit(false);
    }
  };

  return (
    <>
      <Row className={styles.venueProperties}>
        <Col xs={8}>
          <TabBox
            offset
            title={t('venue:venue-info')}
            onChange={onTabChange}
            tabs={tabs}
          >
            <br />
            {edit ? (
              <EditForm
                noCols
                float
                checkOnce
                id={formID}
                error={error}
                loading={loading}
                onSubmit={submit}
                onCancel={() => setEdit(false)}
                // onFieldError={(arr: any) => setFieldErrors(arr)}
              >
                {properties.map((el: any, i: number) =>
                  el.id === 'map' ? (
                    <Upload
                      half
                      key={i}
                      formats={['pdf']}
                      name={'file'}
                      data={data.map}
                      error={
                        (!!error && (!files || !files.length)) ||
                        fieldErrors.indexOf('file') > -1
                      }
                      onChange={(files) => {
                        setFiles(files);
                      }}
                      onError={(error) => {
                        setError(error);
                      }}
                      onRemove={onFileRemove}
                    />
                  ) : (
                    <Input
                      key={i}
                      className={styles.propField}
                      optional //={el.id !== "conference_rooms"}
                      float
                      medium
                      reset
                      full={properties.length < 4 || tab === 'security'}
                      name={el.id}
                      icon={el.id}
                      label={el.label}
                      defaultValue={el.value}
                      type={el.type}
                      format={el.format}
                      unit={el.unit}
                      convertTo={el.convertTo}
                      placeholder={el.placeholder}
                      maxLength={el.maxLength}
                      // error={fieldErrors.indexOf(el.id) > -1}
                      // onChange={(obj) => setError("")}
                    />
                  )
                )}
              </EditForm>
            ) : (
              properties.map((el: any, i: number) =>
                el.id === 'map' &&
                data.map &&
                (!!access || planDataByTab.map) ? (
                  <Download
                    key={i}
                    name={t(`venue:map`)}
                    description={data.map.name}
                    type={data.map.type}
                    icon={data.map.category}
                    url={data.map.url}
                    href={data.map.href}
                  />
                ) : (
                  <Property
                    key={i}
                    float
                    medium
                    // restricted={el.restricted}
                    full={properties.length < 4 || tab === 'security'}
                    icon={el.id}
                    label={el.label}
                    value={el.value}
                    unit={el.unit}
                  />
                )
              )
            )}

            {
              // Restaurants
              tab === 'catering' &&
                !!currRestaurants &&
                !!currRestaurants.length && (
                  <List maxItems={3}>
                    {currRestaurants.map((el: any, i: number) => (
                      <RestaurantListed key={i} data={el} />
                    ))}
                  </List>
                )
            }
          </TabBox>

          <br />

          {!!access && !edit && (
            <Button
              small
              dark
              icon={'edit'}
              label={t(`common:cta-edit`)}
              onClick={() => setEdit(true)}
            />
          )}

          <br />
          <br />
          <br />
          <br />
        </Col>
      </Row>
    </>
  );
}
